/**
 * Outbox sync engine (plain TypeScript, no React).
 *
 * Triggers: reconnect, app foreground, "sync now", a fresh save, and its own retry timer.
 * One run at a time; a trigger during a run asks for one more pass afterwards.
 *
 * Per item, in queue order:
 *   200          → server copy stored, item removed (a newer local edit made meanwhile stays queued)
 *   409+current  → item "conflict", server copy kept; the user picks keep mine (force) / use server copy
 *   400/403/404… → item "failed" with the server's messages; never retried automatically
 *   401          → stop, keep everything, tell the app to show the login screen
 *   5xx/429/408  → item stays "pending" with exponential backoff; later items still go
 *   no answer    → item stays "pending" with backoff; the run stops (the network is down)
 */
import { ApiError, NetworkError, type ApiClient, type SaveSectionBody, type LocationBody } from "./api";
import { Emitter } from "./emitter";
import type { LocalStore, OutboxCounts } from "./store";
import type { OutboxError, OutboxItem } from "./types";

export type SyncTrigger = "manual" | "reconnect" | "foreground" | "save" | "timer" | "startup" | "login";

export type ItemOutcome = "sent" | "conflict" | "failed" | "deferred" | "network" | "unauthenticated";

export interface SyncRunResult {
  sent: number;
  conflicts: number;
  failed: number;
  /** temporary server errors; retried later */
  deferred: number;
  /** why the run ended early, if it did */
  stoppedBy: null | "offline" | "network" | "unauthenticated";
}

export interface SyncStatus {
  running: boolean;
  /** null = unknown (no connectivity report yet) */
  online: boolean | null;
  authRequired: boolean;
  lastRunAt: number | null;
  lastResult: SyncRunResult | null;
  /** when the retry timer will fire (epoch ms), if armed */
  nextRetryAt: number | null;
  counts: OutboxCounts;
}

export interface Timers {
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export interface SyncEngineOptions {
  store: LocalStore;
  api: ApiClient;
  now?: () => number;
  /** 0..1, for backoff jitter */
  random?: () => number;
  timers?: Timers;
  /** first retry delay and the cap, in ms */
  backoff?: { baseMs: number; maxMs: number };
  /** the server said 401 (expired token, or `account_disabled`): show the login screen; the outbox is kept */
  onUnauthenticated?: (error: ApiError | null) => void;
  /** false while logged out: nothing is sent and no 401 is raised (default: always true) */
  canSend?: () => boolean;
}

const EMPTY_RESULT: SyncRunResult = { sent: 0, conflicts: 0, failed: 0, deferred: 0, stoppedBy: null };

/** Exponential backoff with ±20% jitter: base, 2×base, 4×base … capped at max. */
export function backoffDelay(attempt: number, baseMs: number, maxMs: number, random: () => number = Math.random): number {
  const exp = Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt - 1));
  const jitter = 0.8 + 0.4 * random();
  return Math.round(Math.min(maxMs, exp * jitter));
}

export class SyncEngine {
  readonly changes = new Emitter();

  private readonly store: LocalStore;
  private readonly api: ApiClient;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly timers: Timers;
  private readonly backoff: { baseMs: number; maxMs: number };
  private readonly onUnauthenticated?: (error: ApiError | null) => void;
  private lastAuthError: ApiError | null = null;
  private readonly canSend: () => boolean;

  private current: Promise<SyncRunResult> | null = null;
  private rerun = false;
  private rerunIgnoreBackoff = false;
  private rerunIgnoreOffline = false;
  private timer: unknown = null;
  private disposed = false;

  private state: SyncStatus = {
    running: false,
    online: null,
    authRequired: false,
    lastRunAt: null,
    lastResult: null,
    nextRetryAt: null,
    counts: { pending: 0, failed: 0, conflict: 0, total: 0 },
  };

  constructor(opts: SyncEngineOptions) {
    this.store = opts.store;
    this.api = opts.api;
    this.now = opts.now ?? Date.now;
    this.random = opts.random ?? Math.random;
    this.timers = opts.timers ?? { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) };
    this.backoff = opts.backoff ?? { baseMs: 5_000, maxMs: 10 * 60_000 };
    this.onUnauthenticated = opts.onUnauthenticated;
    this.canSend = opts.canSend ?? (() => true);
    // keep the badge counts current after any outbox change (save, keep mine, discard …)
    this.unsubStore = this.store.changes.subscribe((topic) => {
      if (topic === "outbox") this.scheduleCounts();
    });
  }

  private unsubStore: () => void;
  private countsQueued = false;

  private scheduleCounts() {
    if (this.countsQueued || this.disposed) return;
    this.countsQueued = true;
    void Promise.resolve().then(async () => {
      this.countsQueued = false;
      await this.refreshCounts().catch(() => undefined);
    });
  }

  get status(): SyncStatus {
    return this.state;
  }

  private patch(p: Partial<SyncStatus>) {
    this.state = { ...this.state, ...p };
    this.changes.emit();
  }

  async refreshCounts(): Promise<OutboxCounts> {
    const counts = await this.store.counts();
    this.patch({ counts });
    return counts;
  }

  /** Connectivity report from NetInfo. Coming back online syncs right away. */
  setOnline(online: boolean): Promise<SyncRunResult> | null {
    const was = this.state.online;
    if (was === online) return null;
    this.patch({ online });
    if (online && was !== true) return this.trigger("reconnect");
    return null;
  }

  /** After a successful login: sending may continue. */
  resumeAfterLogin(): Promise<SyncRunResult> {
    this.patch({ authRequired: false });
    return this.trigger("login");
  }

  /**
   * Start a run (or join the one in progress). User- and connectivity-driven triggers
   * ignore backoff so "sync now" really tries every pending item now.
   */
  trigger(reason: SyncTrigger): Promise<SyncRunResult> {
    const ignoreBackoff = reason !== "timer" && reason !== "save";
    // "sync now" tries even if NetInfo says offline: its reachability check can be wrong
    // (captive portals, LAN-only servers), and a real outage just fails fast.
    const ignoreOffline = reason === "manual";
    if (this.current) {
      this.rerun = true;
      this.rerunIgnoreBackoff = this.rerunIgnoreBackoff || ignoreBackoff;
      this.rerunIgnoreOffline = this.rerunIgnoreOffline || ignoreOffline;
      return this.current;
    }
    this.current = this.loop(ignoreBackoff, ignoreOffline).finally(() => {
      this.current = null;
    });
    return this.current;
  }

  /** Wait for the run in progress, if any. */
  async idle(): Promise<void> {
    while (this.current) await this.current.catch(() => undefined);
  }

  dispose() {
    this.disposed = true;
    this.unsubStore();
    this.clearTimer();
  }

  private async loop(ignoreBackoff: boolean, ignoreOffline: boolean): Promise<SyncRunResult> {
    this.clearTimer();
    this.patch({ running: true });
    let total: SyncRunResult = { ...EMPTY_RESULT };
    try {
      let ignore = ignoreBackoff;
      let force = ignoreOffline;
      do {
        this.rerun = false;
        this.rerunIgnoreBackoff = false;
        this.rerunIgnoreOffline = false;
        const r = await this.runOnce(ignore, force);
        total = {
          sent: total.sent + r.sent,
          conflicts: total.conflicts + r.conflicts,
          failed: total.failed + r.failed,
          deferred: total.deferred + r.deferred,
          stoppedBy: r.stoppedBy,
        };
        ignore = this.rerunIgnoreBackoff;
        force = this.rerunIgnoreOffline;
        // A trigger that arrived during this pass (reconnect, a new save, "sync now") gets one more
        // pass even if this one stopped on a network error: the network may be back now. Without
        // this, a reconnect during a failing send was dropped and the items waited for the retry
        // timer (up to 10 minutes). Each extra pass is caused by a new trigger, so it cannot loop.
      } while (this.rerun && !this.disposed);
    } finally {
      const counts = await this.store.counts().catch(() => this.state.counts);
      this.patch({ running: false, lastRunAt: this.now(), lastResult: total, counts });
      await this.armTimer().catch(() => undefined);
    }
    return total;
  }

  private async runOnce(ignoreBackoff: boolean, ignoreOffline: boolean): Promise<SyncRunResult> {
    const result: SyncRunResult = { ...EMPTY_RESULT };
    if (this.state.online === false && !ignoreOffline) return { ...result, stoppedBy: "offline" };
    if (this.state.authRequired || !this.canSend()) return { ...result, stoppedBy: "unauthenticated" };

    const dueBy = ignoreBackoff ? Number.MAX_SAFE_INTEGER : this.now();
    let afterId = 0;
    for (;;) {
      if (this.disposed) break;
      const item = await this.store.nextPending(afterId, dueBy);
      if (!item) break;
      afterId = item.id;
      const outcome = await this.sendItem(item);
      if (outcome === "sent") result.sent++;
      else if (outcome === "conflict") result.conflicts++;
      else if (outcome === "failed") result.failed++;
      else if (outcome === "deferred") result.deferred++;
      else if (outcome === "network") {
        result.stoppedBy = "network";
        break;
      } else if (outcome === "unauthenticated") {
        result.stoppedBy = "unauthenticated";
        this.patch({ authRequired: true });
        try {
          this.onUnauthenticated?.(this.lastAuthError);
        } catch {
          /* the app's handler must not break the engine */
        }
        break;
      }
    }
    return result;
  }

  /** Send one outbox item and record the outcome in the store. */
  async sendItem(item: OutboxItem): Promise<ItemOutcome> {
    try {
      if (item.kind === "section") {
        const body: SaveSectionBody = { data: item.payload.data, baseVersion: item.baseVersion, deviceId: item.deviceId };
        if (item.force) body.force = true;
        const saved = await this.api.saveSection(item.assemblyId, item.boothCode, item.section, body);
        await this.store.completeSection(item.id, item.rev, item.assemblyId, item.boothCode, item.section, saved);
      } else {
        const p = item.payload;
        const body: LocationBody = { lat: p.lat, lng: p.lng, capturedAt: p.capturedAt, deviceId: item.deviceId };
        if (p.accuracyM != null) body.accuracyM = p.accuracyM;
        const r = await this.api.shareLocation(item.assemblyId, item.boothCode, body);
        await this.store.completeLocation(item.id, item.assemblyId, item.boothCode, r.location);
      }
      return "sent";
    } catch (e) {
      return this.handleError(item, e);
    }
  }

  private async handleError(item: OutboxItem, e: unknown): Promise<ItemOutcome> {
    const retryAt = () => this.now() + backoffDelay(item.attempts + 1, this.backoff.baseMs, this.backoff.maxMs, this.random);

    if (e instanceof NetworkError) {
      await this.store.recordAttempt(item.id, { code: "network", message: e.message }, retryAt());
      return "network";
    }
    if (e instanceof ApiError) {
      if (e.status === 401) {
        this.lastAuthError = e;
        return "unauthenticated";
      }
      if (e.status === 409 && item.kind === "section" && e.body?.current) {
        await this.store.markConflict(item.id, e.body.current);
        return "conflict";
      }
      if (e.status === 408 || e.status === 429 || e.status >= 500) {
        await this.store.recordAttempt(item.id, { code: e.code, status: e.status, message: e.message }, retryAt());
        return "deferred";
      }
      const err: OutboxError = { code: e.code, status: e.status, message: e.message };
      if (e.messageHi) err.messageHi = e.messageHi;
      const details = e.details;
      if (details.length) err.details = details;
      await this.store.markFailed(item.id, item.rev, err);
      return "failed";
    }
    // Anything else (a bug, a storage hiccup): keep the item and try again later.
    await this.store
      .recordAttempt(item.id, { code: "error", message: e instanceof Error ? e.message : String(e) }, retryAt())
      .catch(() => undefined);
    return "deferred";
  }

  private clearTimer() {
    if (this.timer != null) this.timers.clear(this.timer);
    this.timer = null;
    if (this.state.nextRetryAt != null) this.patch({ nextRetryAt: null });
  }

  /** Arm the retry timer for the earliest backed-off pending item (only while not known offline). */
  private async armTimer() {
    this.clearTimer();
    if (this.disposed || this.state.online === false || this.state.authRequired || !this.canSend()) return;
    const items = await this.store.listOutbox();
    const waits = items.filter((i) => i.status === "pending").map((i) => i.nextAttemptAt);
    if (!waits.length) return;
    const at = Math.max(Math.min(...waits), this.now() + 1_000);
    this.timer = this.timers.set(() => {
      this.timer = null;
      void this.trigger("timer");
    }, at - this.now());
    this.patch({ nextRetryAt: at });
  }
}
